import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { HelpHint } from "@/components/HelpHint";
import { Switch } from "@/components/ui/switch";
import { resetRushRatesCache } from "@/components/Metric";
import ServiceZonesCard from "@/components/ServiceZonesCard";
import ShippingSettingsCard from "@/components/ShippingSettingsCard";
import ApparelSettingsCard from "@/components/ApparelSettingsCard";

const SVC_MODULES = [["paper", "Paper"], ["booklet", "Booklet"], ["large-format", "Large Format"], ["stickers", "Stickers"], ["dtf", "Apparel"], ["laser", "Laser"], ["direct-print", "Direct Print"], ["channel-letters", "Channel Letters"], ["sublimation", "Sublimation"], ["roll-stickers", "Roll Stickers"]];

const GROUPS = [
  {
    title: "Markups",
    fields: [
      { name: "retail_markup_pct", label: "Retail Markup %", hint: "Base retail price = cost × (1 + this %). 200 means retail is 3× your material/production cost." },
      { name: "ws_off_retail_pct", label: "Wholesale = Retail − % (all modules)", hint: "Wholesale price = Retail − this %. Applies to every module unless a material has its own manual wholesale override." },
      { name: "labour_rate", label: "Shop labour $/hr (Services)", hint: "Hourly shop-labour rate used by the Services block (Labour = hours × persons × this rate). Installation/onsite fees are set per Zone below." },
      { name: "misc_markup_default_x", label: "Miscellaneous markup × (default)", hint: "Default sell multiplier for Miscellaneous add-ons: charge = cost × this. Applies to any misc material without its own per-item override. Use “Apply to all” in the Miscellaneous card below to force EVERY misc item to this ×." },
    ],
  },
  {
    title: "Paper Click Charges & Lamination",
    fields: [
      { name: "click_4_0", label: "4/0 Click / sheet (CAD)", hint: "Per-sheet charge for single-sided (4/0) full-color printing — the machine 'click' cost." },
      { name: "click_4_4", label: "4/4 Click / sheet (CAD)", hint: "Per-sheet charge for double-sided (4/4) full-color printing." },
      { name: "double_sided_surcharge_pct", label: "Double-sided (4/4) surcharge %", hint: "Extra % added to a paper job when it is printed double-sided (4/4)." },
      { name: "lamination_per_sheet", label: "Lamination / sheet (CAD)", hint: "Per-sheet charge to laminate a paper print." },
    ],
  },
  {
    title: "Rush Pricing (surcharge %)",
    fields: [
      { name: "rush_same_day_pct", label: "Same day +%", hint: "Surcharge % added to the whole quote when the customer needs the job the same day." },
      { name: "rush_next_day_pct", label: "Next day +%", hint: "Surcharge % added when the customer needs the job the next day." },
    ],
  },
  {
    title: "Round Corners — Paper (per stack)",
    fields: [
      { name: "rc_paper_pieces_per_stack", label: "Pieces per stack", hint: "How many paper sheets are rounded together in one cut (one 'stack')." },
      { name: "rc_paper_per_stack", label: "Cost per stack (CAD)", hint: "Cost to round the corners of one full stack of paper." },
      { name: "rc_paper_min", label: "Minimum charge (CAD)", hint: "Minimum charge for the paper round-corner service, even on small jobs." },
    ],
  },
  {
    title: "Round Corners — Substrate",
    fields: [
      { name: "rc_substrate_pieces_per_stack", label: "Pieces per stack", hint: "How many rigid pieces are rounded together in one cut (one 'stack')." },
      { name: "rc_substrate_per_stack", label: "Cost per stack/piece (CAD)", hint: "Cost to round the corners of one stack (or piece) of rigid substrate." },
      { name: "rc_substrate_min", label: "Minimum charge (CAD)", hint: "Minimum charge for the substrate round-corner service." },
    ],
  },
  {
    title: "Large Format — General (all materials)",
    fields: [
      { name: "lf_print_per_sqft", label: "Print / sqft (CAD)", hint: "Printing service charge per ft², added ON TOP of the material price. It does not replace the material rate. Set to 0 if your material price already includes printing/ink." },
      { name: "tiling_overlap_in", label: "Tiling Overlap (in)", hint: "Overlap in inches added between panels when a print is too wide/tall and gets split into multiple tiles." },
      { name: "lf_cost_labor_per_sqft", label: "True-cost: Labor / ft² (Price Intel)", hint: "Reference ONLY — does NOT change customer quotes. Your estimated in-house labor cost per ft², used in the Price Intel tab to show real margin vs. competitors." },
      { name: "lf_cost_ink_per_sqft", label: "True-cost: Ink / ft² (Price Intel)", hint: "Reference ONLY — does NOT change customer quotes. Your estimated ink cost per ft², used in the Price Intel tab for margin analysis." },
    ],
  },
  {
    title: "Large Format — Vinyl",
    note: "Shown only when the selected material type is Vinyl.",
    fields: [
      { name: "lf_diecut_transfer_per_sqft", label: "Die-Cut / sqft (CAD)", hint: "Die-cut / contour-cut labor per ft². Charged when Die-Cut is checked on a piece." },
      { name: "lf_transfer_tape_per_sqft", label: "Transfer Tape / sqft (CAD)", hint: "Application / transfer tape charge per ft². Charged when Transfer Tape is checked on a piece." },
    ],
  },
  {
    title: "Large Format — Vinyl & Poster",
    note: "Lamination applies to both Vinyl and Poster. Decoration has no rate here — it uses the selected decoration vinyl's own $/ft².",
    fields: [
      { name: "lf_lamination_per_sqft", label: "Lamination / sqft (CAD)", hint: "Lamination LABOR per ft², added ON TOP of the laminate material's own rate. Total laminated = this + the laminate roll's $/ft²." },
    ],
  },
  {
    title: "Large Format — Banner",
    note: "Flat finishing prices for Banner. Entered as retail; wholesale is Retail − the global wholesale %.",
    fields: [
      { name: "lf_banner_reinforced_per_linear_ft", label: "Reinforced edges / linear ft (CAD)", hint: "Double-sided tape / reinforced hem charged around the full perimeter. Price = perimeter (ft) × this rate. e.g. a 4×10 ft banner = 28 ft × rate." },
      { name: "lf_grommet_price_each", label: "Grommet / each (CAD)", hint: "Price per grommet. Named options set the count (Top Center 1, Top Corners 2, 4 Corners 4, 4+2 Middle 6); 'Every 2 ft' = perimeter(ft) ÷ 2 grommets." },
    ],
  },
  {
    title: "Booklet Binding",
    note: "Toggle each option on/off — only enabled bindings appear in the Booklets module.",
    fields: [
      { name: "binding_saddle", label: "Saddle Stitch (CAD ea.)", enableKey: "binding_saddle_on", hint: "Price per booklet for saddle-stitch binding (staples on the fold)." },
      { name: "binding_spiral", label: "Spiral (CAD ea.)", enableKey: "binding_spiral_on", hint: "Price per booklet for spiral / coil binding." },
      { name: "binding_wireo", label: "Wire-O (CAD ea.)", enableKey: "binding_wireo_on", hint: "Price per booklet for Wire-O (double-loop wire) binding." },
      { name: "binding_perfect", label: "Perfect Binding (CAD ea.)", enableKey: "binding_perfect_on", hint: "Price per booklet for perfect binding (glued square spine)." },
    ],
  },
  {
    title: "Apparel — DTF",
    fields: [
      { name: "dtf_per_sqft", label: "DTF / ft² (CAD)", hint: "Cost per ft² of DTF transfer film printed." },
      { name: "dtf_labor_per_shirt", label: "Labor / shirt (CAD)", hint: "Labor to heat-press / apply the transfer onto each garment." },
      { name: "dtf_roll_width", label: "DTF roll width (in)", hint: "Printable width of the DTF film roll, used to nest the artwork." },
    ],
  },
  {
    title: "Apparel — Embroidery",
    fields: [
      { name: "embroidery_per_1000_stitches", label: "Per 1,000 stitches (CAD)", hint: "Run charge for every 1,000 stitches in the design." },
      { name: "embroidery_digitizing_1_3", label: "Embroidery setup (1–3 logos) (CAD)", hint: "One-time setup / digitizing fee per order, per group of up to 3 embroidered logos. Can be waived for repeat orders." },
      { name: "apparel_custom_thread_fee", label: "Custom thread colour (CAD each)", hint: "Charged once per order for each custom (non-stock) thread colour." },
    ],
  },
  {
    title: "Apparel — Fees",
    note: "Flat retail charges; wholesale = Retail − the global wholesale %.",
    fields: [
      { name: "apparel_big_size_upcharge", label: "Big-size upcharge / piece (CAD)", hint: "Added to every piece in a big size (2XL and up — list editable below)." },
      { name: "apparel_supplied_setup", label: "Customer-supplied setup / order (CAD)", hint: "One-time setup when the customer brings their own garments." },
      { name: "apparel_design_fee", label: "Design / artwork fee (CAD)", hint: "Optional per-quote fee for creating or fixing the artwork." },
      { name: "apparel_sheet_w", label: "DTF sheet width (in)", hint: "Sheet used for customer-supplied pieces and gang-sheet sales." },
      { name: "apparel_sheet_h", label: "DTF sheet height (in)", hint: "Sheet used for customer-supplied pieces and gang-sheet sales." },
    ],
  },
  {
    title: "Laser",
    fields: [
      { name: "laser_cut_per_linear_ft", label: "Cut / linear ft (CAD)", hint: "Charge per linear foot the laser actually cuts." },
      { name: "laser_engraving_per_sqin", label: "Engraving / in² (CAD)", hint: "Charge per square inch of engraved area." },
      { name: "laser_setup", label: "Laser setup (CAD)", hint: "One-time setup fee added to each laser job." },
    ],
  },
  {
    title: "Direct Print & CNC",
    fields: [
      { name: "directprint_per_sqft", label: "UV print / ft² (CAD)", hint: "Cost per ft² for flatbed / UV direct printing onto rigid substrate." },
      { name: "cnc_cut_per_linear_ft", label: "CNC cut / linear ft (CAD)", hint: "Charge per linear foot the CNC router cuts." },
    ],
  },
  {
    title: "Channel Letters",
    fields: [
      { name: "channel_return_depth_in", label: "Return depth (in)", hint: "Depth (inches) of the letter's side wall ('return'), used to compute material area." },
      { name: "channel_fixture_margin_in", label: "Fixture margin per side (in)", hint: "Extra material margin added per side when fabricating each letter." },
      { name: "channel_letter_labor", label: "Labor / letter (CAD)", hint: "Labor cost to fabricate one channel letter." },
    ],
  },
  {
    title: "Sticker Finishing",
    fields: [
      { name: "sticker_kisscut_per_sqft", label: "Kiss-cut / ft² (CAD)", hint: "Kiss-cut finishing per ft² — cuts the vinyl but leaves the backing intact." },
      { name: "sticker_diecut_per_sqft", label: "Die-cut / ft² (CAD)", hint: "Die-cut finishing per ft² — cuts through the whole sheet, including backing." },
      { name: "sticker_individual_cut_per_piece", label: "Individual cut / piece (CAD)", hint: "Extra charge per sticker when each piece is cut out individually." },
      { name: "sticker_transfer_tape_per_piece", label: "Transfer tape / piece (CAD)", hint: "Fixed transfer-tape charge per sticker × the number of pieces in the order. Separate from Large Format's per-ft² tape." },
      { name: "sticker_laminate_per_sqft", label: "Lamination / ft² (CAD)", hint: "Lamination charge per ft² for stickers." },
    ],
  },
  {
    title: "Sublimation (F570)",
    fields: [
      { name: "sublimation_paper_width", label: "Paper roll width (in)", hint: "Width of the sublimation transfer paper roll, used to nest the artwork." },
      { name: "sublimation_paper_length_ft", label: "Paper roll length (ft)", hint: "Total length of the transfer paper roll, used to amortize the roll cost per ft²." },
      { name: "sublimation_paper_roll_cost", label: "Paper roll cost (CAD)", hint: "Cost of one full roll of sublimation transfer paper." },
      { name: "sublimation_ink_per_sqft", label: "Ink / ft² (CAD)", hint: "Sublimation ink cost per ft² printed." },
      { name: "sublimation_labor_per_unit", label: "Labor / unit (CAD)", hint: "Labor to press / finish each sublimated unit." },
    ],
  },
  {
    title: "Roll Stickers (C6000A)",
    fields: [
      { name: "rollsticker_waste_pieces", label: "Waste pieces / job", hint: "Number of stickers wasted per job for setup and alignment." },
      { name: "rollsticker_cleaning_cost", label: "Ink cleaning / job (CAD)", hint: "Ink / printhead cleaning cost charged once per job." },
      { name: "rollsticker_ink_per_sticker", label: "Ink / sticker (CAD)", hint: "Ink cost for each individual roll sticker." },
      { name: "rollsticker_labor", label: "Labor / job (CAD)", hint: "Labor cost charged once per roll-sticker job." },
      { name: "rollsticker_stickers_per_min", label: "Stickers / minute", hint: "Print speed (stickers per minute), used to estimate production time." },
    ],
  },
  {
    title: "Maintenance & Labor",
    fields: [
      { name: "technician_hourly_rate", label: "Technician hourly rate (CAD/hr)", hint: "Hourly rate used to cost machine maintenance and technician labor." },
    ],
  },
];

const MODULES_VD = [
  { k: "default", l: "Default (all modules)" },
  { k: "paper", l: "Paper Printing" },
  { k: "booklet", l: "Booklets" },
  { k: "large-format", l: "Large Format" },
  { k: "stickers", l: "Stickers" },
  { k: "dtf", l: "Apparel (DTF + Embroidery)" },
  { k: "laser", l: "Laser" },
  { k: "direct-print", l: "Direct Print & CNC" },
  { k: "channel-letters", l: "Channel Letters" },
  { k: "sublimation", l: "Sublimation" },
  { k: "roll-stickers", l: "Roll Stickers" },
];

const GROUP_SECTION = {
  "Markups": "general",
  "Rush Pricing (surcharge %)": "general",
  "Maintenance & Labor": "general",
  "Paper Click Charges & Lamination": "paper",
  "Round Corners — Paper (per stack)": "paper",
  "Round Corners — Substrate": "large-format",
  "Large Format — General (all materials)": "large-format",
  "Large Format — Vinyl": "large-format",
  "Large Format — Vinyl & Poster": "large-format",
  "Large Format — Banner": "large-format",
  "Booklet Binding": "booklet",
  "Apparel — DTF": "dtf",
  "Apparel — Embroidery": "dtf",
  "Apparel — Fees": "dtf",
  "Laser": "laser",
  "Direct Print & CNC": "direct-print",
  "Channel Letters": "channel-letters",
  "Sticker Finishing": "stickers",
  "Sublimation (F570)": "sublimation",
  "Roll Stickers (C6000A)": "roll-stickers",
};

const SECTIONS = [
  { k: "general", l: "General" },
  { k: "paper", l: "Paper" },
  { k: "booklet", l: "Booklet" },
  { k: "large-format", l: "Large Format" },
  { k: "stickers", l: "Stickers" },
  { k: "dtf", l: "Apparel" },
  { k: "laser", l: "Laser" },
  { k: "direct-print", l: "Direct Print & CNC" },
  { k: "channel-letters", l: "Channel Letters" },
  { k: "sublimation", l: "Sublimation" },
  { k: "roll-stickers", l: "Roll Stickers" },
  { k: "shipping", l: "Shipping" },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [vdModule, setVdModule] = useState("default");
  const [section, setSection] = useState("general");
  const [miscStats, setMiscStats] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  useEffect(() => {
    if (!s) return;
    const x = Number(s.misc_markup_default_x || 0);
    const t = setTimeout(() => {
      api.get("/materials/misc-markup-stats", { params: { default_x: x } }).then((r) => setMiscStats(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [s?.misc_markup_default_x]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const payload = { ...s };
      Object.keys(payload).forEach((k) => { if (typeof payload[k] === "string" && k !== "currency" && !k.startsWith("ship_from_")) payload[k] = Number(payload[k]); });
      if (Array.isArray(payload.volume_discounts)) payload.volume_discounts = payload.volume_discounts.map((r) => ({ qty: Number(r.qty) || 0, pct: Number(r.pct) || 0 }));
      if (payload.volume_discounts_by_module && typeof payload.volume_discounts_by_module === "object") {
        const m = {}; Object.entries(payload.volume_discounts_by_module).forEach(([k, arr]) => { m[k] = (arr || []).map((r) => ({ qty: Number(r.qty) || 0, pct: Number(r.pct) || 0 })); }); payload.volume_discounts_by_module = m;
      }
      if (Array.isArray(payload.lf_size_curve)) payload.lf_size_curve = payload.lf_size_curve.map((r) => ({ max_sqft: Number(r.max_sqft) || 0, rate: Number(r.rate) || 0, flat: Number(r.flat) || 0 }));
      if (Array.isArray(payload.lf_volume_mult_by_ft)) payload.lf_volume_mult_by_ft = payload.lf_volume_mult_by_ft.map((r) => ({ ft: Number(r.ft) || 0, mult: Number(r.mult) || 0 }));
      const { data } = await api.put("/settings", payload);
      setS(data);
      resetRushRatesCache();
      toast.success("Settings saved — all quotes now use these values");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!s) return null;

  const applyMiscAll = async () => {
    const x = Number(s.misc_markup_default_x || 0);
    if (!(x > 0)) return toast.error("Set a markup × greater than 0 first");
    if (!window.confirm(`Set the sell markup to ×${x} on ALL miscellaneous materials? This overwrites each item's current ×.`)) return;
    try {
      const { data } = await api.post("/materials/misc-markup-apply-all", { markup_x: x });
      toast.success(`Applied ×${x} to ${data.updated} miscellaneous material${data.updated === 1 ? "" : "s"}.`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };

  const vdMap = s.volume_discounts_by_module || {};
  const vd = vdMap[vdModule] || [];
  const writeVd = (arr) => setS({ ...s, volume_discounts_by_module: { ...vdMap, [vdModule]: arr } });
  const setVD = (i, key, val) => writeVd(vd.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addVD = () => writeVd([...vd, { qty: 0, pct: 0 }]);
  const removeVD = (i) => writeVd(vd.filter((_, idx) => idx !== i));
  const copyDefault = () => writeVd((vdMap.default || []).map((r) => ({ ...r })));
  const usingDefault = vdModule !== "default" && vd.length === 0;
  const vdDisabledList = Array.isArray(s.volume_discounts_disabled_modules) ? s.volume_discounts_disabled_modules : [];
  const vdModuleOn = !vdDisabledList.includes(vdModule);
  const toggleVdModule = (on) => {
    const set = new Set(vdDisabledList);
    if (on) set.delete(vdModule); else set.add(vdModule);
    setS({ ...s, volume_discounts_disabled_modules: [...set] });
  };
  const vdStatus = vdModule === "default"
    ? (vdModuleOn
        ? "These Default tiers apply to every module that has no custom tiers and isn't turned Off."
        : "Default is Off — modules with no custom tiers of their own get no volume discount.")
    : (!vdModuleOn
        ? `Off — ${MODULES_VD.find((m) => m.k === vdModule)?.l || "this module"} has no volume discount for its orders.`
        : (vd.length > 0
            ? `On — ${MODULES_VD.find((m) => m.k === vdModule)?.l || "this module"} uses its own custom tiers below.`
            : `On — ${MODULES_VD.find((m) => m.k === vdModule)?.l || "this module"} falls back to the Default tiers. Add tiers to customize.`));

  const curve = Array.isArray(s.lf_size_curve) ? s.lf_size_curve : [];
  const setCurve = (arr) => setS({ ...s, lf_size_curve: arr });
  const setCv = (i, key, val) => setCurve(curve.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addCv = () => setCurve([...curve, { max_sqft: 0, rate: 3.1, flat: 0 }]);
  const rmCv = (i) => setCurve(curve.filter((_, idx) => idx !== i));

  const vtab = Array.isArray(s.lf_volume_mult_by_ft) ? s.lf_volume_mult_by_ft : [];
  const setVtab = (arr) => setS({ ...s, lf_volume_mult_by_ft: arr });
  const setVt = (i, key, val) => setVtab(vtab.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addVt = () => setVtab([...vtab, { ft: (vtab.length ? (Number(vtab[vtab.length - 1].ft) || 0) + 1 : 1), mult: 1 }]);
  const rmVt = (i) => setVtab(vtab.filter((_, idx) => idx !== i));

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Global pricing — changes flow through every module">
        <Button data-testid="save-settings-button" onClick={save} className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-sm">
          <Save size={16} className="mr-2" />Save
        </Button>
      </PageHeader>
      <div className="p-8 flex gap-6 items-start">
        <nav className="w-48 shrink-0 sticky top-4 space-y-1" data-testid="settings-nav">
          {SECTIONS.map((sec) => (
            <button key={sec.k} data-testid={`settings-nav-${sec.k}`} onClick={() => setSection(sec.k)}
              className={`w-full text-left text-sm px-3 py-2 rounded-sm transition-colors ${section === sec.k ? "bg-[#2495D3] text-white font-semibold" : "text-slate-600 hover:bg-slate-100"}`}>
              {sec.l}
            </button>
          ))}
        </nav>
        <div className="flex-1 min-w-0 space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
        {GROUPS.filter((g) => (GROUP_SECTION[g.title] || "general") === section).map((g) => (
          <div key={g.title} className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-head font-bold mb-1">{g.title}</h3>
            {g.note && <p className="text-[11px] text-slate-500 mb-3">{g.note}</p>}
            <div className="space-y-3">
              {g.fields.map((f) => {
                const off = f.enableKey && s[f.enableKey] === false;
                return (
                <div key={f.name}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 flex-1">
                      {f.enableKey && (
                        <Switch
                          data-testid={`toggle-${f.enableKey}`}
                          checked={s[f.enableKey] !== false}
                          onCheckedChange={(v) => setS({ ...s, [f.enableKey]: v })}
                        />
                      )}
                      <Label className={`text-xs ${off ? "text-slate-400 line-through" : ""}`}>{f.label}</Label>
                    </div>
                    <Input
                      data-testid={`setting-${f.name}`}
                      type="number" step="0.01"
                      value={s[f.name]}
                      disabled={off}
                      onChange={(e) => setS({ ...s, [f.name]: e.target.value })}
                      className={`rounded-sm w-32 num text-right ${off ? "opacity-40" : ""}`}
                    />
                  </div>
                  {f.hint && <p className="text-[10px] text-slate-400 mt-1 leading-snug" data-testid={`hint-${f.name}`}>{f.hint}</p>}
                </div>
                );
              })}
            </div>
          </div>
        ))}
        </div>

        {section === "dtf" && <ApparelSettingsCard s={s} setS={setS} />}

        {section === "general" && (<div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="volume-discounts-card">
          <div className="flex items-center justify-between mb-1 gap-3">
            <h3 className="font-head font-bold">Volume Discounts</h3>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2" data-testid="vd-module-toggle">
                <Switch checked={vdModuleOn} onCheckedChange={toggleVdModule} data-testid="vd-module-enabled" />
                <span className="text-[11px] text-slate-500">{vdModuleOn ? "On" : "Off"}</span>
              </div>
              <select data-testid="vd-module-select" value={vdModule} onChange={(e) => setVdModule(e.target.value)} className="text-xs border border-slate-200 rounded-sm h-8 px-2 bg-white">
                {MODULES_VD.map((m) => <option key={m.k} value={m.k}>{m.l}</option>)}
              </select>
              <Button data-testid="vd-add" onClick={addVD} disabled={!vdModuleOn} variant="outline" className="rounded-sm h-8 text-xs"><Plus size={14} className="mr-1" />Add tier</Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-2">Buy more → cheaper. The highest tier whose quantity ≤ order quantity applies — to Retail and Wholesale. Set tiers <b>per module</b>; a module with no tiers uses <b>Default</b>. Turn the switch <b>Off</b> to disable volume discounts entirely for the selected module.</p>
          {vdStatus && <p className="text-[11px] font-semibold text-[#2495D3] mb-3" data-testid="vd-status">{vdStatus}</p>}
          {!vdModuleOn ? (
            <div data-testid="vd-disabled-note" className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">Volume discounts are <b>Off</b> for this module — orders are billed at full price with no quantity discount (and no fallback to Default).</div>
          ) : (
          <>
          {vdModule !== "default" && <div className="mb-3"><Button data-testid="vd-copy-default" onClick={copyDefault} variant="ghost" className="h-7 text-xs text-[#2495D3]">Copy Default tiers as a starting point</Button></div>}
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] font-mono uppercase text-slate-400"><th className="text-left py-1">Quantity ≥</th><th className="text-left py-1">Discount %</th><th /></tr></thead>
            <tbody>
              {vd.map((r, i) => (
                <tr key={i} data-testid="vd-row">
                  <td className="py-1 pr-3"><Input data-testid="vd-qty" type="number" value={r.qty} onChange={(e) => setVD(i, "qty", e.target.value)} className="rounded-sm w-32 num" /></td>
                  <td className="py-1 pr-3"><Input data-testid="vd-pct" type="number" step="0.5" value={r.pct} onChange={(e) => setVD(i, "pct", e.target.value)} className="rounded-sm w-28 num" /></td>
                  <td className="py-1 text-right"><button data-testid="vd-remove" onClick={() => removeVD(i)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button></td>
                </tr>
              ))}
              {vd.length === 0 && <tr><td colSpan={3} className="py-4 text-center text-slate-400 text-xs">{usingDefault ? "No custom tiers — this module uses the Default tiers." : "No tiers — add one to enable volume discounts."}</td></tr>}
            </tbody>
          </table>
          </>
          )}
        </div>
        )}

        {section === "large-format" && (
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="lf-pricing-card">
          <h3 className="font-head font-bold mb-1">Large Format &amp; Stickers — Pricing engine</h3>
          <p className="text-xs text-slate-500 mb-5">Price = material rate <b>X ($/ft²)</b> (each material's override, or its system cost × markup) × the <b>volume multiplier</b> for the roll-feet used. Large Format then adds a per-piece premium; Stickers within the minimum band charge the flat minimum. Applies to every material automatically.</p>

          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div>
              <Label className="text-xs flex items-center gap-1">Per-piece extra % (LF)<HelpHint side="top">Each additional identical piece adds this % of the base price. Rewards single-design runs while still charging for extra pieces.</HelpHint></Label>
              <Input data-testid="lf-piece-extra-pct" type="number" step="1" value={s.lf_piece_extra_pct ?? 65} onChange={(e) => setS({ ...s, lf_piece_extra_pct: e.target.value })} className="rounded-sm num mt-1" />
              <p className="text-[10px] text-slate-400 mt-1">Each extra identical piece adds this % of the base price.</p>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">Minimum charge ($)<HelpHint side="top">The floor price for one section (12&quot; × roll width) in Large Format &amp; Stickers. Small jobs never price below this.</HelpHint></Label>
              <Input data-testid="lf-section-min-charge" type="number" step="0.01" value={s.lf_section_min_charge ?? 20} onChange={(e) => setS({ ...s, lf_section_min_charge: e.target.value })} className="rounded-sm num mt-1" />
              <p className="text-[10px] text-slate-400 mt-1">1 section (12" × roll width) — LF &amp; Stickers.</p>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">Sticker min band (in)<HelpHint side="top">Sticker jobs that fit within this height pay the flat minimum charge instead of the per-ft² rate.</HelpHint></Label>
              <Input data-testid="sticker-min-band-in" type="number" step="0.5" value={s.sticker_min_band_in ?? 12} onChange={(e) => setS({ ...s, sticker_min_band_in: e.target.value })} className="rounded-sm num mt-1" />
              <p className="text-[10px] text-slate-400 mt-1">Sticker fills within this height pay the flat minimum.</p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-1 gap-3">
            <h4 className="text-sm font-semibold">Volume rate by roll-feet</h4>
            <Button data-testid="lf-vt-add" onClick={addVt} variant="outline" className="rounded-sm h-8 text-xs"><Plus size={14} className="mr-1" />Add tier</Button>
          </div>
          <p className="text-xs text-slate-500 mb-3">More feet of material → lower $/ft². The multiplier scales the material rate X (1.00 = full price). The applied tier is the largest <b>Feet ≥</b> the roll-feet used.</p>
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] font-mono uppercase text-slate-400"><th className="text-left py-1">Feet (≥)</th><th className="text-left py-1">× material rate</th><th className="text-left py-1">= $/ft² (example @ $6)</th><th /></tr></thead>
            <tbody>
              {vtab.map((r, i) => (
                <tr key={i} data-testid="lf-vt-row">
                  <td className="py-1 pr-3"><Input data-testid="lf-vt-ft" type="number" step="1" value={r.ft} onChange={(e) => setVt(i, "ft", e.target.value)} className="rounded-sm w-24 num" /></td>
                  <td className="py-1 pr-3"><Input data-testid="lf-vt-mult" type="number" step="0.0001" value={r.mult} onChange={(e) => setVt(i, "mult", e.target.value)} className="rounded-sm w-28 num" /></td>
                  <td className="py-1 pr-3 text-slate-500 font-mono text-xs">${((Number(r.mult) || 0) * 6).toFixed(2)}</td>
                  <td className="py-1 text-right"><button data-testid="lf-vt-remove" onClick={() => rmVt(i)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button></td>
                </tr>
              ))}
              {vtab.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400 text-xs">No tiers — every order uses ×1.00 (full material rate).</td></tr>}
            </tbody>
          </table>
        </div>
        )}

        {section === "shipping" && <ShippingSettingsCard />}

        {section === "general" && (<div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="misc-markup-card">
          <h3 className="font-head font-bold mb-1">Miscellaneous — bulk markup</h3>
          <p className="text-xs text-slate-500 mb-4">Each Miscellaneous add-on sells at <b>cost × markup</b>. Set the default × in <b>Markups</b> above (used when an item has no override), or push a single × to <b>every</b> misc item at once here. You can still fine-tune any item later in the Materials page.</p>
          <div className="flex items-center gap-3">
            <Label className="text-xs flex items-center gap-1">Apply ×<HelpHint side="top">Sets one markup multiplier and pushes it to every Miscellaneous item at once (cost × this number). You can still override individual items later.</HelpHint></Label>
            <Input type="number" step="0.1" data-testid="misc-bulk-x" value={s.misc_markup_default_x ?? 2} onChange={(e) => setS({ ...s, misc_markup_default_x: e.target.value })} className="rounded-sm w-24 num text-right" />
            <Button data-testid="misc-bulk-apply" onClick={applyMiscAll} variant="outline" className="rounded-sm h-9 text-xs">Apply to all miscellaneous materials</Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Tip: type the × you want in the box, then click Apply. Remember to also Save (top-right) so the default × is stored for new items.</p>
          {miscStats && (
            <div className="mt-3 rounded-sm bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600 space-y-1.5" data-testid="misc-markup-stats">
              <div>
                Applies to <b>{miscStats.total}</b> miscellaneous material{miscStats.total === 1 ? "" : "s"} ·{" "}
                <span className="text-emerald-700 font-semibold">{miscStats.equal} at ×{Number(s.misc_markup_default_x || 0)}</span> ·{" "}
                <span className="text-amber-700 font-semibold">{miscStats.above} above</span> ·{" "}
                <span className="text-blue-700 font-semibold">{miscStats.below} below</span>
                {(miscStats.above + miscStats.below) > 0 && <> · <b>{miscStats.above + miscStats.below}</b> with their own ×</>}
              </div>
              {miscStats.custom?.length > 0 && (
                <details className="rounded-sm border border-slate-200 bg-white p-2" data-testid="misc-markup-custom">
                  <summary className="cursor-pointer font-semibold text-slate-600">Show {miscStats.custom.length} item{miscStats.custom.length === 1 ? "" : "s"} that differ from the default ×</summary>
                  <ul className="mt-1.5 space-y-0.5">
                    {miscStats.custom.map((i, idx) => (
                      <li key={idx} className="flex justify-between gap-2" data-testid={`misc-custom-${idx}`}>
                        <span className="truncate">{i.name}</span>
                        <span className={`num shrink-0 ${i.markup_x > Number(s.misc_markup_default_x || 0) ? "text-amber-700" : "text-blue-700"}`}>×{i.markup_x}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        )}

        {section === "general" && <ServiceZonesCard />}

        {section === "general" && (<div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="services-toggles-card">
          <h3 className="font-head font-bold mb-1">Services availability per module</h3>
          <p className="text-xs text-slate-500 mb-4">Turn the <b>Services</b> block (Labour / Onsite / Installation / Delivery) on or off for each estimating module.</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {SVC_MODULES.map(([k, l]) => (
              <div key={k} className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2">
                <Label className="text-xs">{l}</Label>
                <Switch data-testid={`toggle-services_${k}_on`} checked={s[`services_${k}_on`] !== false} onCheckedChange={(v) => setS({ ...s, [`services_${k}_on`]: v })} />
              </div>
            ))}
          </div>
        </div>
        )}
        </div>
      </div>
    </div>
  );
}
